'use client';

import { Subject, Chapter } from '@/hooks/useSubject';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { mdiCheckBold } from '@mdi/js';
import Icon from '@mdi/react';
import { useLayoutEffect, useState } from 'react';

const ChapterProgressIndicator = ({
    subject,
    chapter,
}: {
    subject: Subject;
    chapter: Chapter;
}) => {
    const [progress, setProgress] = useState<number>(0);

    const { subjectProgress } = useSubjectProgress(subject.id);

    // Only show after initial render to get rid of 'html doesn't match server' error
    useLayoutEffect(() => {
        const visitedPages: number[] = chapter.page_ids.filter((pageId) => {
            return subjectProgress.pages[pageId];
        });
        if (visitedPages.length === 0) setProgress(0);
        else
            setProgress(
                Math.round(
                    (visitedPages.length / chapter.page_ids.length) * 100,
                ),
            );
    }, [chapter, subjectProgress]);

    let progressColor =
        'bg-primary-500/25 md:bg-primary-500/20 text-primary-100 md:text-primary-text-lg-aa';
    if (progress === 100) progressColor = 'bg-green-600 text-white';

    return (
        <span
            className={`${progressColor} flex shrink-0 h-6 w-6 items-center justify-center overflow-hidden rounded-full text-xs`}
        >
            {progress === 100 && (
                <>
                    <Icon path={mdiCheckBold} size={0.8} />
                    <span className="sr-only">voltooid</span>
                </>
            )}
            {progress > 0 && progress < 100 && (
                <>
                    <svg
                        className="h-full w-full -rotate-90"
                        viewBox="0 0 120 120"
                    >
                        <circle
                            cx="60"
                            cy="60"
                            r="30"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="60"
                            className="transition-all duration-1000 ease-in-out"
                            style={{
                                strokeDashoffset: `${(100 - progress) * 1.8}`,
                                strokeDasharray: '180',
                            }}
                        />
                    </svg>
                    <span className="sr-only">{progress}%</span>
                </>
            )}
        </span>
    );
};

export default ChapterProgressIndicator;
